import { useCallback } from "react";

import { useGetSsoProviderByCodeMutation } from "@/api/auth/useAuthAPI";
import { CheckProviderResponse, SsoAuthorizeErrorInfo } from "@/types/auth";
import { createPkceParameters } from "@/utils/pkceUtils";
import { getSsoCallbackUrl, storeProviderConfig } from "@/utils/sso";

/**
 * Kicks off SSO by hitting the IdP `/authorize` directly (the OAuth entry point):
 * resolve our provider config, generate PKCE (verifier kept in this origin's
 * `sessionStorage`), then navigate to `/authorize`.
 *
 * - If an `sso_session` already exists, `/authorize` 302s straight to our
 *   `/sso/callback?code` (no login shown — true SSO).
 * - Otherwise `/authorize` redirects to the auth app login UI (echoing these
 *   params), which after credentials 302s back to our callback.
 *
 * Init and callback have to live in the same app for that reason: `sessionStorage`
 * is per-origin, so splitting them works only while both share one origin and
 * breaks silently under per-subdomain hosting.
 *
 * Also exposes `redirectToAuthLogin` for the callback to recover from a failed
 * flow: it rebuilds the same authorize params (fresh PKCE, since the previous
 * verifier is already burned) and sends the browser to the auth app *login*
 * page. Pass `errorInfo` only when the IdP or our own BFF actually reported an
 * error so the auth app can surface it; leave it out for client-side
 * precondition failures (missing code / mismatched state), where the internal
 * reason isn't useful to the user.
 */
const useSsoLogin = () => {
  const { mutateAsync: getSsoProviderByCode } =
    useGetSsoProviderByCodeMutation();

  // Fetch and validate the provider config for this client.
  // The endpoint can answer 200 with no usable provider (SSO not configured for
  // this client). Treat that as "no SSO" by throwing, so callers fall back to the
  // local login instead of navigating to a broken /authorize URL.
  const fetchSsoProvider =
    useCallback(async (): Promise<CheckProviderResponse> => {
      const provider = await getSsoProviderByCode(
        import.meta.env.VITE_APP_CODE
      );

      if (!provider?.auth_service_url) {
        throw new Error("SSO provider is not configured for this client");
      }

      return provider;
    }, [getSsoProviderByCode]);

  // Resolve the provider config and cache it, so the callback can redeem the
  // short-lived `code` immediately without a second providers round trip.
  const resolveSsoProvider =
    useCallback(async (): Promise<CheckProviderResponse> => {
      const provider = await fetchSsoProvider();
      storeProviderConfig(provider);
      return provider;
    }, [fetchSsoProvider]);

  // Resolve the provider config and cache it so the callback can exchange the code
  // immediately, without a second providers round-trip.
  const buildAuthorizeParams = useCallback(
    async (
      provider: CheckProviderResponse,
      redirectTo: string
    ): Promise<URLSearchParams> => {
      const { code_challenge, code_challenge_method, state } =
        await createPkceParameters();

      return new URLSearchParams({
        client_id: provider.client_id,
        app_code: import.meta.env.VITE_APP_CODE,
        response_type: "code",
        redirect_uri: getSsoCallbackUrl(),
        scope: provider.scopes.join(" "),
        state,
        code_challenge,
        code_challenge_method,
        redirect_to: redirectTo
      });
    },
    []
  );

  const startSso = useCallback(
    async (redirectTo: string = globalThis.location.href) => {
      const provider = await resolveSsoProvider();
      const params = await buildAuthorizeParams(provider, redirectTo);
      globalThis.location.href = `${provider.auth_service_url}?${params.toString()}`;
    },
    [resolveSsoProvider, buildAuthorizeParams]
  );

  const redirectToAuthLogin = useCallback(
    async (
      redirectTo: string = globalThis.location.href,
      errorInfo?: SsoAuthorizeErrorInfo
    ) => {
      const provider = await resolveSsoProvider();
      const params = await buildAuthorizeParams(provider, redirectTo);

      // Only forward an error when the IdP or BFF actually reported one;
      // precondition failures pass no errorInfo and land on a clean login.
      if (errorInfo) {
        params.set("error", errorInfo.error);
        if (errorInfo.errorDescription) {
          params.set("error_description", errorInfo.errorDescription);
        }
      }

      globalThis.location.href = `${import.meta.env.VITE_APP_AUTH_URL}/login?${params.toString()}`;
    },
    [resolveSsoProvider, buildAuthorizeParams]
  );

  return { startSso, redirectToAuthLogin, fetchSsoProvider };
};

export default useSsoLogin;
